/**
 * The spectacle prescription dialog.
 *
 * Two steps: Rx -> review & issue. The Rx - powers, intermediate and near ADDs, prisms and PD - is
 * pre-filled from today's refraction where there is one (an older refraction is offered, never filled
 * in by itself, and its comment is shown with it), so the happy path is: Continue, Issue. The lens
 * recommendations printed for the patient and the notes are on the review step; what the glasses are
 * for is chosen at dispensing, with the pair being made.
 *
 * Plus or minus cylinder is taken as written, and either eye can be a balance lens. The Rx is checked
 * against the last spectacle prescription: what changed is shown, and a big change or an unlikely
 * value is flagged to look at before issuing - never refused.
 *
 * Only a draft is opened here. An issued prescription is locked - it isn't opened again, and a change
 * is a new prescription - and one found to be wrong is withdrawn from the prescriptions tile, with a
 * reason. Issuing saves the patient's copy to their documents; the prescriber is whoever issues it.
 * Shares the dispensing dialogs' shell and styling, and saves through LensPrescribingStore.
 */
app.controller("lensRxGlassesModalController", [
	"$scope",
	"$mdDialog",
	"$timeout",
	"$animate",
	"$element",
	"$filter",
	"LensPrescribingStore",
	"LensDispensingStore",
	"PatientVisionAPI",
	"CurrentPatient",
	"toast",
	"existingRx",
	function ($scope, $mdDialog, $timeout, $animate, $element, $filter, Rx, Store, PatientVisionAPI, CurrentPatient, toast, existingRx) {
		$scope.R = Rx;
		$scope.S = Store;

		// See the dispensing dialogs: steps switch instantly and fade themselves in, without ngAnimate.
		$animate.enabled(angular.element($element[0].querySelector(".lens-shell")), false);

		const patient = CurrentPatient.patient || {};
		$scope.patient = patient;
		$scope.patientName = [patient.firstName, patient.lastName].filter(Boolean).join(" ") || "Patient";
		$scope.initials = ((patient.firstName || "P").charAt(0) + (patient.lastName || "").charAt(0)).toUpperCase();

		$scope.steps = [
			{ key: "rx", label: "Prescription", icon: "fa-eye" },
			{ key: "issue", label: "Review & issue", icon: "fa-check-square-o" },
		];
		const ISSUE = 1;

		// A renewal arrives as an unsaved draft, so it is new though it comes filled in.
		const isNew = !existingRx || !existingRx.id;
		$scope.p = existingRx ? angular.copy(existingRx) : Rx.emptyGlassesRx();
		const p = $scope.p;
		const rx = p.rx;

		$scope.locked = !Rx.isDraft(p);
		// Whoever issues it is the prescriber; until then that's the user writing it.
		if (!$scope.locked) rx.prescriber = Rx.myName();
		// A draft picked up on a later day is good for the full term from when it is issued.
		Rx.restartExpiry(p);
		$scope.myRegistration = Rx.myRegistration();
		$scope.visited = { rx: true };
		$scope.dirty = false;

		// ------------------------------------------------------------------
		// Navigation
		// ------------------------------------------------------------------
		$scope.current = existingRx && !isNew && ($scope.locked || Rx.hasPowers(rx)) ? ISSUE : 0;
		if ($scope.current === ISSUE) $scope.steps.forEach((s) => ($scope.visited[s.key] = true));

		$scope.go = function (i) {
			if (i < 0 || i >= $scope.steps.length) return;
			$scope.current = i;
			$scope.visited[$scope.steps[i].key] = true;
			const body = $element[0].querySelector(".settings-pane-body");
			if (body) body.scrollTop = 0;
		};
		$scope.next = () => $scope.go($scope.current + 1);
		$scope.back = () => $scope.go($scope.current - 1);

		$scope.stepDone = function (key) {
			switch (key) {
				case "rx": return $scope.rxValid();
				case "issue": return !Rx.isDraft(p);
			}
			return false;
		};

		$scope.progress = function () {
			const done = $scope.steps.filter((s) => $scope.stepDone(s.key)).length;
			return Math.round((done / $scope.steps.length) * 100);
		};

		$scope.stepSummary = function (key) {
			switch (key) {
				case "rx": return $scope.rxValid() ? `R ${eyeShort(rx.od)}  L ${eyeShort(rx.os)}${hasAdd() ? "  Add " + $scope.fmtSph(rx.od.add || rx.os.add) : ""}` : "Enter the Rx";
				case "issue":
					if (Rx.isWithdrawn(p)) return `Withdrawn ${$filter("date")(p.withdrawn && p.withdrawn.at, "d MMM yyyy")}`;
					return Rx.isIssued(p) ? `Issued ${$filter("date")(p.issuedAt, "d MMM yyyy")}` : "Recommendations · check & issue";
			}
		};
		const eyeShort = (e) => (e.balance ? "balance" : $scope.fmtSph(e.sph));

		$scope.status = () => Rx.statusOf(patient.id, p);
		$scope.statusLabel = () => (Rx.isDraft(p) ? (p.id ? "Draft" : "New") : $scope.status().label);

		$scope.$watch("p", (n, o) => {
			if (n !== o) $scope.dirty = true;
		}, true);

		// ------------------------------------------------------------------
		// Step 1 - prescription
		// ------------------------------------------------------------------
		$scope.hBases = ["", "In", "Out"];
		$scope.vBases = ["", "Up", "Down"];
		$scope.eyes = [{ key: "od", label: "Right", sub: "OD" }, { key: "os", label: "Left", sub: "OS" }];
		$scope.vision = { loading: true, data: null, date: null, today: false, comments: [] };

		// The comment written with the refraction, shown beside it when it is offered. The response
		// carries each eye's comments with who and when; the newest one is the refraction's.
		function latestRefractionComment(eye) {
			const list = ((eye && eye.refraction_comments) || []).filter((c) => c && c.comment);
			if (!list.length) return null;
			const newest = list.reduce((a, b) => (new Date(a.date_time || 0) >= new Date(b.date_time || 0) ? a : b));
			return newest.comment;
		}
		function refractionComments(v) {
			const od = latestRefractionComment(v && v.od);
			const os = latestRefractionComment(v && v.os);
			if (od && od === os) return [{ eye: "", text: od }];
			return [od ? { eye: "R", text: od } : null, os ? { eye: "L", text: os } : null].filter(Boolean);
		}

		const isNum = (v) => v !== null && v !== undefined && v !== "" && !isNaN(v);
		const hasAdd = () => [rx.od, rx.os].some((e) => !e.balance && Number(e.add) > 0);
		const steps = (v, step) => Math.abs(Number(v) / step - Math.round(Number(v) / step)) < 1e-9;
		const within = (v, lo, hi) => Number(v) >= lo && Number(v) <= hi;

		$scope.fmtSph = function (v) {
			if (!isNum(v)) return "-";
			const n = Number(v);
			return (n > 0 ? "+" : n < 0 ? "" : "") + n.toFixed(2).replace(/^-?0\.00$/, "0.00");
		};
		// A balance eye keeps whatever powers are written, so they are shown with the flag.
		$scope.fmtEye = function (e) {
			if (!e || !isNum(e.sph)) return e && e.balance ? "Balance" : "-";
			let s = $scope.fmtSph(e.sph);
			if (isNum(e.cyl) && Number(e.cyl) !== 0) s += ` / ${$scope.fmtSph(e.cyl)} × ${e.axis || "?"}`;
			else s += " DS";
			if (Number(e.intAdd) > 0) s += `  Int ${$scope.fmtSph(e.intAdd)}`;
			if (Number(e.add) > 0) s += `  Add ${$scope.fmtSph(e.add)}`;
			const prism = Store.fmtPrism(e);
			if (prism) s += `  Prism ${prism}`;
			return e.balance ? `Balance (${s})` : s;
		};
		$scope.fmtPd = function () {
			const mono = ["od", "os"].filter((k) => isNum(rx[k].pd));
			const parts = [];
			if (isNum(rx.pd)) parts.push(`${rx.pd} binocular`);
			if (mono.length) parts.push(`R ${isNum(rx.od.pd) ? rx.od.pd : "-"} / L ${isNum(rx.os.pd) ? rx.os.pd : "-"} monocular`);
			return parts.length ? `${parts.join(" · ")} (mm)` : "";
		};

		// What is wrong with one field of an eye, in words, or null. Every number input keeps what was
		// typed (allowInvalid), so nothing is dropped silently: it is said here instead.
		function fieldProblem(e, field) {
			const v = e[field];
			const has = isNum(v);
			if (v !== null && v !== undefined && v !== "" && !has) return "isn't a number";
			switch (field) {
				case "sph": return !has ? (e.balance ? null : "is needed") : !within(v, -30, 30) || !steps(v, 0.25) ? "goes from -30.00 to +30.00 in 0.25 steps" : null;
				case "cyl": return has && (!within(v, -20, 20) || !steps(v, 0.25)) ? "goes from -20.00 to +20.00 in 0.25 steps" : null;
				case "axis": return isNum(e.cyl) && Number(e.cyl) !== 0 && !(has && within(v, 1, 180) && steps(v, 1)) ? "is needed, 1 to 180, wherever there is cylinder" : null;
				case "add": return Store.addProblem(v);
				case "intAdd": return Store.addProblem(v);
				case "hPrism":
				case "vPrism": {
					const base = field === "hPrism" ? e.hBase : e.vBase;
					if (has && (!within(v, 0, 20) || !steps(v, 0.25))) return "goes from 0 to 20 prism dioptres in 0.25 steps";
					return (Number(v) > 0) !== !!base ? "needs its base, and a base its prism" : null;
				}
				case "pd": return has && (!within(v, 20, 45) || !steps(v, 0.5)) ? "(monocular) goes from 20 to 45 mm in 0.5 steps" : null;
			}
			return null;
		}
		const FIELD_NAMES = { sph: "sphere", cyl: "cylinder", axis: "axis", add: "near ADD", intAdd: "intermediate ADD", hPrism: "horizontal prism", vPrism: "vertical prism", pd: "PD" };
		const FIELDS = Object.keys(FIELD_NAMES);

		// A balance eye needs no sphere (fieldProblem waives it), but whatever is written there is
		// still checked - the values stay on the prescription.
		function eyeProblems(k) {
			const e = rx[k];
			return FIELDS.map((f) => ({ field: f, text: fieldProblem(e, f) })).filter((x) => x.text);
		}

		const binocularPdProblem = () => (isNum(rx.pd) || (rx.pd !== null && rx.pd !== undefined && rx.pd !== "")) && !(isNum(rx.pd) && within(rx.pd, 40, 85) && steps(rx.pd, 0.5))
			? "The binocular PD goes from 40 to 85 mm in 0.5 steps."
			: null;

		$scope.rxValid = () => ["od", "os"].every((k) => !eyeProblems(k).length) && !(rx.od.balance && rx.os.balance) && !binocularPdProblem();

		// The first thing to fix, said under the table.
		$scope.rxHint = function () {
			if (rx.od.balance && rx.os.balance) return "Only one eye can have a balance lens.";
			// A sphere not entered yet isn't an error until the user has moved on.
			const shown = (problem) => !(problem.field === "sph" && problem.text === "is needed" && !$scope.visited.issue);
			const eye = $scope.eyes.find((e) => eyeProblems(e.key).some(shown));
			if (eye) {
				const problem = eyeProblems(eye.key).find(shown);
				return `${eye.label} ${FIELD_NAMES[problem.field]} ${problem.text}.`;
			}
			return binocularPdProblem() || "Sphere is needed for both eyes (or mark one as a balance lens), and an axis (1-180) wherever there is cylinder.";
		};

		$scope.rxError = function (eye, field) {
			if (field === "bpd") return !!binocularPdProblem();
			const e = rx[eye];
			const text = fieldProblem(e, field);
			if (field === "sph" && text === "is needed") return !!$scope.visited.issue;
			return !!text;
		};

		// Balance only flags the eye - the lab makes a lens to match the other. The powers written
		// there stay, and can still be changed.
		$scope.toggleBalance = function (k) {
			if ($scope.locked) return;
			rx[k].balance = !rx[k].balance;
		};

		// Turns the whole Rx between plus and minus cylinder - the same lenses written the other way.
		$scope.hasCyl = () => ["od", "os"].some((k) => !rx[k].balance && isNum(rx[k].cyl) && Number(rx[k].cyl) !== 0);
		$scope.transposeRx = function () {
			if ($scope.locked) return;
			["od", "os"].forEach(function (k) {
				if (rx[k].balance) return;
				const t = Store.transpose(rx[k]);
				angular.extend(rx[k], { sph: t.sph, cyl: t.cyl, axis: t.axis });
			});
		};

		function num(v) {
			return isNum(v) ? Number(v) : null;
		}

		// One eye of the vision module's refraction, as an Rx. Its prisms are kept apart by direction,
		// as here, with bases written BASE_IN and so on.
		const VISION_BASES = { BASE_IN: "In", BASE_OUT: "Out", BASE_UP: "Up", BASE_DOWN: "Down" };
		function eyeFromVision(src) {
			src = src || {};
			const hPrism = num(src.horizontal_prism) || null;
			const vPrism = num(src.vertical_prism) || null;
			return {
				sph: num(src.refraction_sphere),
				cyl: num(src.refraction_cylinder),
				axis: num(src.refraction_axis),
				intAdd: num(src.refraction_intermediate_add),
				add: num(src.refraction_add),
				hPrism: hPrism,
				hBase: hPrism ? VISION_BASES[src.horizontal_prism_base] || "" : "",
				vPrism: vPrism,
				vBase: vPrism ? VISION_BASES[src.vertical_prism_base] || "" : "",
				balance: false,
			};
		}
		// The refraction notice also says which distance each eye's prism was measured at - the Rx
		// itself doesn't carry it, so it is read straight off the vision record.
		const PRISM_DISTANCE_WORDS = { DISTANCE: "distance", INTERMEDIATE: "intermediate", NEAR: "near" };
		$scope.visionEye = function (k) {
			const src = ($scope.vision.data || {})[k] || {};
			const eye = eyeFromVision(src);
			let s = $scope.fmtEye(eye);
			if (src.prism_distance && Store.fmtPrism(eye)) s += ` (at ${PRISM_DISTANCE_WORDS[src.prism_distance] || src.prism_distance.toLowerCase()})`;
			return s;
		};
		$scope.visionPd = () => num(($scope.vision.data || {}).pupillary_distance);

		// Whether the Rx fields hold exactly what the refraction would fill in - powers, adds, prisms
		// and PD. Then the notice says it is pre-filled and the use-refraction button has nothing left
		// to do, so it is hidden. Fields edited away from the refraction (or a draft or renewal that
		// came with its own values) bring the button back as the way to apply the whole refraction.
		const sameValue = (a, b) => (isNum(a) ? isNum(b) && Number(a) === Number(b) : !isNum(b));
		$scope.refractionApplied = function () {
			const v = $scope.vision.data;
			if (!v) return false;
			const pd = $scope.visionPd();
			if (pd && (!isNum(rx.pd) || Number(rx.pd) !== Number(pd))) return false;
			return ["od", "os"].every(function (k) {
				const from = eyeFromVision(v[k]);
				const eye = rx[k];
				return (
					["sph", "cyl", "axis", "intAdd", "add", "hPrism", "vPrism"].every((f) => sameValue(from[f], eye[f])) &&
					(!from.hPrism || from.hBase === eye.hBase) &&
					(!from.vPrism || from.vBase === eye.vBase)
				);
			});
		};

		// The vision module records one binocular PD, and it is kept as one: halving it would print two
		// monocular PDs that were never measured.
		$scope.applyRefraction = function () {
			const v = $scope.vision.data;
			if (!v || $scope.locked) return;
			["od", "os"].forEach((k) => angular.extend(rx[k], eyeFromVision(v[k])));
			const pd = $scope.visionPd();
			if (pd) rx.pd = pd;
			rx.source = $scope.vision.today ? "Today's refraction" : $scope.vision.date ? `Refraction of ${moment($scope.vision.date).format("D MMM YYYY")}` : "Earlier refraction";
			if (hasAdd()) rx.use = "multifocal";
		};

		// Start from an earlier prescription's powers, from the history under the refraction.
		$scope.useEarlier = function (earlier) {
			if ($scope.locked) return;
			Rx.copyRx(rx, earlier);
		};

		$scope.copyRightToLeft = function () {
			if ($scope.locked) return;
			rx.os = angular.copy(rx.od);
		};

		$scope.onAddChange = function () {
			if (hasAdd() && (rx.use === "distance" || rx.use === "near")) rx.use = "multifocal";
		};

		// The last spectacle prescription, what has changed since, and anything worth a second look.
		$scope.previous = () => Rx.previousOf(patient.id, p);
		$scope.changes = function () {
			const prev = $scope.previous();
			return prev && $scope.rxValid() ? Rx.changesFrom(prev, p) : null;
		};
		// A boolean for ng-if: changes() is a new object on every digest.
		$scope.hasChanges = () => !!$scope.changes();
		$scope.warnings = () => ($scope.rxValid() ? Rx.rxWarnings(p, $scope.previous()) : []);

		function hasRefraction(v) {
			return v && ["od", "os"].some((k) => v[k] && isNum(v[k].refraction_sphere));
		}

		if (!patient.id) $scope.vision.loading = false;
		else PatientVisionAPI.getPatientVision(patient.id).then(
			function (resp) {
				const v = resp.data && resp.data.vision;
				$scope.vision.loading = false;
				if (hasRefraction(v)) {
					$scope.vision.data = v;
					$scope.vision.comments = refractionComments(v);
					const audit = v.od_vision_audit && v.od_vision_audit.refraction_sphere;
					$scope.vision.date = audit ? new Date(audit) : null;
					$scope.vision.today = !!$scope.vision.date && moment($scope.vision.date).isSame(moment(), "day");
					// A new prescription starts from the refraction done today - an older one is only offered.
					if (isNew && $scope.vision.today && !Rx.hasPowers(rx) && !isNum(rx.od.sph) && !isNum(rx.os.sph)) {
						$scope.applyRefraction();
						$timeout(() => ($scope.dirty = false));
					}
				}
			},
			function () {
				$scope.vision.loading = false;
			}
		);

		// ------------------------------------------------------------------
		// Step 2 - review & issue
		// ------------------------------------------------------------------
		// Lens recommendations are printed on the patient's copy, so another dispenser has them too.
		// Clicking a chip adds it; the text is free. Grouped so the long list stays scannable.
		$scope.recommendationGroups = [
			{
				label: "Lens form",
				chips: [
					"Progressive recommended",
					"Bifocal recommended",
					"Single vision - distance",
					"Single vision - reading",
					"Office / occupational lens",
					"Separate reading pair",
					"Degressive reading lens",
				],
			},
			{
				label: "Material & thickness",
				chips: [
					"High index - strong Rx",
					"Impact-resistant lenses (polycarbonate or Trivex)",
					"Safety eyewear to AS/NZS 1337",
					"Aspheric design for flatter profile",
				],
			},
			{
				label: "Coatings & treatments",
				chips: [
					"Anti-reflection coating",
					"Anti-reflection for screen work",
					"Blue light filter",
					"Scratch-resistant hard coat",
					"Anti-fog treatment",
					"UV protection",
				],
			},
			{
				label: "Tints & sun",
				chips: [
					"Photochromic for outdoors",
					"Polarised sunglasses",
					"Prescription sunglasses as second pair",
					"Tint for light sensitivity",
				],
			},
			{
				label: "Fit & wear",
				chips: [
					"Prism as prescribed - do not adapt",
					"Fit with increased vertex distance in mind",
					"Wear full time",
					"Wear for driving",
				],
			},
		];
		// The practice's own dispensing extras - the coatings, lens types, tints and mirror finishes it
		// sells, under their own names - join the standing chips, so a recommendation can name exactly
		// what dispensing offers. One a standing chip already covers isn't repeated.
		Store.loadSettings().then(function () {
			const group = (label) => $scope.recommendationGroups.find((g) => g.label === label);
			const covered = (name) => $scope.recommendationGroups.some((g) => g.chips.some((c) => c.toLowerCase().indexOf(name.toLowerCase()) !== -1));
			const add = function (label, name) {
				if (name && !covered(name)) group(label).chips.push(name);
			};
			// "Standard", "Clear" and "None" are the no-op choices, not recommendations.
			const offered = (list) => Store[list].filter((i) => ["standard", "clear", "none"].indexOf(i.id) === -1);
			offered("COATINGS").forEach((c) => add("Coatings & treatments", c.name));
			offered("LENS_TYPES").forEach((t) => add("Tints & sun", t.name));
			offered("TINTS").forEach((t) => add("Tints & sun", /tint/i.test(t.name) ? t.name : `${t.name} tint`));
			offered("MIRRORS").forEach((m) => add("Tints & sun", /mirror/i.test(m.name) ? m.name : `${m.name} mirror`));
		}, angular.noop);

		$scope.hasRecommendation = (a) => (p.recommendations || "").split(/\s*;\s*/).indexOf(a) !== -1;
		$scope.toggleRecommendation = function (a) {
			if ($scope.locked) return;
			const parts = (p.recommendations || "").split(/\s*;\s*/).filter(Boolean);
			const i = parts.indexOf(a);
			if (i >= 0) parts.splice(i, 1);
			else parts.push(a);
			p.recommendations = parts.join("; ");
		};

		const today = () => {
			const d = new Date();
			d.setHours(0, 0, 0, 0);
			return d;
		};
		const expiryProblem = () => (!rx.expires ? "Rx expiry date" : new Date(rx.expires) < today() ? "an expiry date that hasn't passed" : null);

		// What stops it being issued, each with the step that fixes it. The same list while nothing changes:
		// the template repeats over it, and a fresh list of fresh objects every digest never settles.
		let missingKept = { json: null, list: [] };
		$scope.missing = function () {
			const out = [];
			if (!$scope.rxValid()) out.push({ label: "Prescription", step: 0 });
			if (expiryProblem()) out.push({ label: expiryProblem(), step: ISSUE });
			const json = angular.toJson(out);
			if (json !== missingKept.json) missingKept = { json: json, list: out };
			return missingKept.list;
		};
		$scope.canIssue = () => !$scope.missing().length && !$scope.issuing && !$scope.locked;

		$scope.issuing = false;
		$scope.saving = false;

		const failMessage = (resp, fallback) => toast.create({ timeout: 6000, message: Store.errorMessage(resp, fallback), className: "alert-danger", dismissible: true });

		$scope.issue = function () {
			if (!$scope.canIssue()) return;
			$scope.issuing = true;
			Rx.issue(patient.id, p, null)
				// Issued is the end of the dialog's work: it closes, as a saved draft does. The server files the
				// patient's copy in their documents; the prescription is issued whether or not that works.
				.then(function (issued) {
					if (!Rx.copyFiled(issued)) copyNotSaved();
					$mdDialog.hide(issued);
				})
				.catch((resp) => failMessage(resp, "The prescription could not be issued."))
				.finally(() => ($scope.issuing = false));
		};

		$scope.saveDraft = function () {
			if ($scope.saving) return;
			$scope.saving = true;
			Rx.save(patient.id, p)
				.then((saved) => $mdDialog.hide(saved))
				.catch((resp) => failMessage(resp, "The draft could not be saved."))
				.finally(() => ($scope.saving = false));
		};

		const copyNotSaved = () =>
			toast.create({ timeout: 8000, message: "The prescription was issued, but its copy couldn't be saved to the patient's documents. Open it from Prescriptions to print it.", className: "alert-warning", dismissible: true });

		// ------------------------------------------------------------------
		// Close
		// ------------------------------------------------------------------
		$scope.confirmClose = false;
		// A function, not "confirmClose = false" in the template: the prompt sits in an ng-if, whose
		// child scope would get its own confirmClose and leave this one - and the prompt - as they were.
		$scope.keepEditing = () => ($scope.confirmClose = false);
		$scope.close = function () {
			if (($scope.dirty || (isNew && existingRx)) && !$scope.locked && Rx.hasPowers(rx)) {
				$scope.confirmClose = true;
				return;
			}
			$mdDialog.cancel();
		};
		$scope.discard = () => $mdDialog.cancel();
	},
]);
